import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import { dataTestIds } from 'src/constants/data-test-ids';
import { getApiError } from 'utils/lib/helpers/oystehrApi';
import { MedicalRecordDateRange, MedicalRecordDateSpan } from 'utils/lib/types/data/get-patient-medical-record.types';

interface MedicalRecordDateRangeDialogProps {
  open: boolean;
  confirmText: string;
  recordDates?: MedicalRecordDateSpan;
  loading: boolean;
  loadFailed: boolean;
  onClose: () => void;
  onConfirm: (dateRange: MedicalRecordDateRange) => Promise<void>;
}

export const MedicalRecordDateRangeDialog: FC<MedicalRecordDateRangeDialogProps> = ({
  open,
  confirmText,
  recordDates,
  loading,
  loadFailed,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const [from, setFrom] = useState<DateTime | null>(null);
  const [to, setTo] = useState<DateTime | null>(null);
  const [message, setMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }
    if (initialized) return;
    setMessage(undefined);
    if (loading || loadFailed || !recordDates) {
      setFrom(null);
      setTo(null);
      return;
    }
    const today = DateTime.now().startOf('day');
    const latestDay = recordDates.latest ? DateTime.fromISO(recordDates.latest).startOf('day') : today;
    setFrom(recordDates.earliest ? DateTime.fromISO(recordDates.earliest).startOf('day') : today);
    setTo(latestDay > today ? latestDay : today);
    setInitialized(true);
  }, [open, initialized, loading, loadFailed, recordDates]);

  const isValid = !!from?.isValid && !!to?.isValid && from.startOf('day') <= to.startOf('day');
  const confirm = async (): Promise<void> => {
    if (!from?.isValid || !to?.isValid) return;

    setSubmitting(true);
    setMessage(undefined);

    try {
      await onConfirm({
        from: from.startOf('day').toISO()!,
        to: to.endOf('day').toISO()!,
      });
      onClose();
    } catch (error) {
      setMessage(
        getApiError({
          error,
          defaultError: 'The medical record could not be prepared. Please try again.',
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const datePicker = (
    label: string,
    value: DateTime | null,
    onChange: (value: DateTime | null) => void,
    testId: string
  ): JSX.Element => (
    <DesktopDatePicker
      label={label}
      value={value}
      onChange={(next) => {
        onChange(next);
        setMessage(undefined);
      }}
      format="MM/dd/yyyy"
      disabled={!initialized}
      slotProps={{ textField: { fullWidth: true, inputProps: { 'data-testid': testId } } }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      data-testid={dataTestIds.medicalRecordDateRangeDialog.root}
    >
      <DialogTitle sx={{ color: theme.palette.primary.dark, fontWeight: 600, fontSize: '24px' }}>
        Medical Record
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={submitting}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1">Dates predefined to include all available records.</Typography>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            {datePicker('From', from, setFrom, dataTestIds.medicalRecordDateRangeDialog.fromInput)}
            {datePicker('To', to, setTo, dataTestIds.medicalRecordDateRangeDialog.toInput)}
          </Box>
        </LocalizationProvider>
        {!initialized && loadFailed && !loading && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid={dataTestIds.medicalRecordDateRangeDialog.loadError}>
            Couldn't load the record dates. Close the dialog and try again.
          </Alert>
        )}
        {message && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid={dataTestIds.medicalRecordDateRangeDialog.message}>
            {message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={{ borderRadius: '100px', textTransform: 'none', fontWeight: 500 }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting || (!initialized && !loadFailed)}
          disabled={!initialized || !isValid}
          onClick={() => void confirm()}
          sx={{ borderRadius: '100px', textTransform: 'none', fontWeight: 500 }}
          data-testid={dataTestIds.medicalRecordDateRangeDialog.confirmButton}
        >
          {confirmText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
